import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ShipmentSummary } from '../types';

const STATUS_COLORS: Record<string, string> = {
  Created: 'bg-gray-100 text-gray-800',
  PickedUpByCarrier: 'bg-blue-100 text-blue-800',
  OnDelivery: 'bg-amber-100 text-amber-800',
  Delivered: 'bg-green-100 text-green-800',
  Cancelled: 'bg-red-100 text-red-800',
  ActionRequired: 'bg-orange-100 text-orange-800',
  AddressCorrectionRequired: 'bg-yellow-100 text-yellow-800',
  UnableToDeliver: 'bg-red-100 text-red-800',
};

interface ShipmentsTableProps {
  shipments: ShipmentSummary[];
  isLoading: boolean;
  onSelect: (number: string) => void;
  sortBy: string;
  sortOrder: -1 | 1;
  onSort: (key: string) => void;
}

function SortHeader({
  label,
  sortKey,
  currentSortBy,
  currentSortOrder,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortBy: string;
  currentSortOrder: -1 | 1;
  onSort: (key: string) => void;
}) {
  return (
    <TableHead className="cursor-pointer select-none" onClick={() => onSort(sortKey)}>
      {label} {currentSortBy === sortKey && (currentSortOrder === 1 ? '↑' : '↓')}
    </TableHead>
  );
}

function formatAddr(full: string): string {
  // "Company, Street, Suite, City, Postal, Province, Country" → "Company, City"
  const parts = full.split(', ');
  if (parts.length >= 4) {
    return `${parts[0]}, ${parts[parts.length - 3]}`;
  }
  return full;
}

export function ShipmentsTable({
  shipments,
  isLoading,
  onSelect,
  sortBy,
  sortOrder,
  onSort,
}: ShipmentsTableProps) {
  if (isLoading) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground">
        Loading shipments...
      </div>
    );
  }

  if (shipments.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground">
        No shipments found
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <SortHeader label="Shipment Date" sortKey="shipmentDate" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <TableHead>Number</TableHead>
            <TableHead>Ref</TableHead>
            <TableHead>Service</TableHead>
            <TableHead>Origin</TableHead>
            <TableHead>Destination</TableHead>
            <TableHead>Pkgs</TableHead>
            <TableHead>Status</TableHead>
            <SortHeader label="Status Date" sortKey="statusDate" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {shipments.map((s) => (
            <TableRow
              key={s.number}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onSelect(s.number)}
            >
              <TableCell className="whitespace-nowrap text-sm">
                {format(new Date(s.shipmentDate), 'yyyy-MM-dd HH:mm')}
              </TableCell>
              <TableCell className="font-mono text-xs">{s.number}</TableCell>
              <TableCell className="text-sm">{s.aliasReference1Value || '—'}</TableCell>
              <TableCell className="text-sm max-w-[200px] truncate">{s.serviceName}</TableCell>
              <TableCell className="text-sm max-w-[180px] truncate" title={s.fullOriginAddress}>
                {formatAddr(s.fullOriginAddress)}
              </TableCell>
              <TableCell className="text-sm max-w-[180px] truncate" title={s.fullDestinationAddress}>
                {formatAddr(s.fullDestinationAddress)}
              </TableCell>
              <TableCell className="text-center">{s.packageQuantity}</TableCell>
              <TableCell>
                <Badge className={STATUS_COLORS[s.packageStatusCode] || 'bg-gray-100 text-gray-800'}>
                  {s.packageStatusName}
                </Badge>
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                {format(new Date(s.statusDate), 'yyyy-MM-dd HH:mm')}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
